import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../store/authSlice";
import { buildAdminDashboardPath, getDefaultDashboardTabForRole } from "../router/dashboardNavigation";
import { normalizeRole } from "../utils/rbacHelper";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import logoImage from "../assets/logo.png";
import loginImage from "../assets/loginimage.png";

const AUTH_FONT_FAMILY = '"Inter", "Segoe UI", Arial, sans-serif';
const BRAND_GRADIENT = "linear-gradient(90deg, #22C55E 0%, #2563EB 100%)";
const DESKTOP_PANEL_RADIUS = "0 100% 100% 0 / 0 46% 46% 0";
const DESKTOP_CURVE_VERTICAL_BLEED = 120;
const SHORT_DESKTOP_QUERY = "@media (min-width:1200px) and (max-height:640px)";
const FEATURE_ITEMS = [
  { icon: ShieldOutlinedIcon, title: "Secure & Reliable" },
  { icon: AccessTimeOutlinedIcon, title: "Real-time Tracking" },
  { icon: QueryStatsOutlinedIcon, title: "Advanced Reports" },
  { icon: FmdGoodOutlinedIcon, title: "Geo Fencing" },
];
const WEB_APP_BLOCKED_ROLES = new Set(["viewer", "driver", "field_emp"]);
const WEB_APP_BLOCKED_MESSAGE = "Please login on app";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const blockedMessage = sessionStorage.getItem("webLoginBlockedMessage");
    if (!blockedMessage) return;

    setError(blockedMessage);
    sessionStorage.removeItem("webLoginBlockedMessage");
  }, []);

  const identifierHint = useMemo(() => {
    if (!identifier) return "User ID / Email / Mobile";
    const looksLikeEmail = identifier.includes("@");
    const looksLikePhone = /^[0-9+\-\s()]{7,}$/.test(identifier);
    if (looksLikeEmail) return "Looks like Email";
    if (looksLikePhone) return "Looks like Mobile";
    return "Looks like User ID";
  }, [identifier]);

  const IdentifierIcon = useMemo(() => {
    if (!identifier) return PersonOutlineOutlinedIcon;
    const looksLikeEmail = identifier.includes("@");
    const looksLikePhone = /^[0-9+\-\s()]{7,}$/.test(identifier);
    if (looksLikeEmail) return AlternateEmailOutlinedIcon;
    if (looksLikePhone) return PhoneIphoneOutlinedIcon;
    return PersonOutlineOutlinedIcon;
  }, [identifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${backendUrl}/auth/login`, {
        identifier,
        password,
      });

      const { token, user } = res.data;
      const normalizedRole = normalizeRole(user?.role);

      if (WEB_APP_BLOCKED_ROLES.has(normalizedRole)) {
        sessionStorage.setItem("webLoginBlockedMessage", WEB_APP_BLOCKED_MESSAGE);
        setError(WEB_APP_BLOCKED_MESSAGE);
        setPassword("");
        return;
      }

      dispatch(loginSuccess({ user, token }));

      const role = normalizedRole || user.role;
      navigate(
        buildAdminDashboardPath({
          tab: getDefaultDashboardTabForRole(role),
        }),
        { replace: true }
      );

      setMessage("Login successful!");
    } catch (err) {
      setError(err.response?.data?.msg || "Invalid credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { lg: "100vh" },
        overflow: { xs: "hidden", lg: "auto" },
        bgcolor: "#f6f8fb",
        fontFamily: AUTH_FONT_FAMILY,
      }}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          height: { lg: "100vh" },
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", lg: "min(48vw, 720px)", xl: "min(45vw, 760px)" },
            minHeight: { xs: "100vh", lg: "100vh" },
            bgcolor: "#ffffff",
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
            overflow: { xs: "hidden", lg: "visible" },
            boxShadow: { lg: "28px 0 60px rgba(15, 23, 42, 0.06)" },
            zIndex: 2,
            [SHORT_DESKTOP_QUERY]: {
              width: "min(52vw, 700px)",
            },
            "&::before": {
              content: '""',
              display: { xs: "none", lg: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: { lg: "-152px", xl: "-182px" },
              width: { lg: "248px", xl: "308px" },
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "transparent",
              borderRight: "6px solid rgba(34, 197, 94, 0.96)",
              borderRadius: DESKTOP_PANEL_RADIUS,
              pointerEvents: "none",
              zIndex: 4,
              [SHORT_DESKTOP_QUERY]: {
                right: "-118px",
                width: "194px",
              },
            },
            "&::after": {
              content: '""',
              display: { xs: "none", lg: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: { lg: "-146px", xl: "-176px" },
              width: { lg: "236px", xl: "296px" },
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "#ffffff",
              borderRadius: DESKTOP_PANEL_RADIUS,
              pointerEvents: "none",
              zIndex: 3,
              [SHORT_DESKTOP_QUERY]: {
                right: "-112px",
                width: "182px",
              },
            },
          }}
        >
	          <Box
	            sx={{
	              position: "relative",
	              zIndex: 5,
	              width: "100%",
                minWidth: 0,
	              maxWidth: 620,
	              px: { xs: 3, sm: 5, lg: 5, xl: 6 },
	              py: { xs: 4, sm: 5, lg: 4.5, xl: 6 },
	              display: "flex",
	              flexDirection: "column",
	              justifyContent: { xs: "space-between", lg: "flex-start", xl: "space-between" },
                alignItems: "stretch",
	              gap: { xs: 3, lg: 2.4, xl: 4 },
                [SHORT_DESKTOP_QUERY]: {
                  paddingTop: 22,
                  paddingBottom: 18,
                  paddingLeft: 28,
                  paddingRight: 48,
                  gap: 12,
                },
	            }}
	          >
	            <Stack
                spacing={{ xs: 4, lg: 3.2, xl: 5 }}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  alignItems: "stretch",
                  [SHORT_DESKTOP_QUERY]: {
                    gap: 12,
                  },
                }}
              >
	              <Stack direction="row" spacing={2.2} alignItems="center">
                <Box
                  component="img"
                  src={logoImage}
                  alt="GPS Controller"
	                  sx={{
	                    width: { xs: 74, sm: 88, lg: 88, xl: 108 },
	                    height: { xs: 74, sm: 88, lg: 88, xl: 108 },
	                    objectFit: "contain",
	                    flexShrink: 0,
                      [SHORT_DESKTOP_QUERY]: {
                        width: 64,
                        height: 64,
                      },
	                  }}
	                />
                <Stack spacing={0.3}>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 22, sm: 28, lg: 29, xl: 34 },
	                      lineHeight: 1,
	                      fontWeight: 800,
	                      letterSpacing: "-0.04em",
	                      color: "#0F2740",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 22,
                        },
	                    }}
	                  >
                    GPS
                  </Typography>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 20, sm: 25, lg: 28, xl: 33 },
	                      lineHeight: 1,
	                      fontWeight: 700,
	                      letterSpacing: "-0.03em",
	                      color: "#20B374",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 22,
                        },
	                    }}
	                  >
                    CONTROLLER
                  </Typography>
                  <Typography
	                    sx={{
	                      pt: 1,
	                      color: "#667085",
	                      fontSize: { xs: 14, sm: 15, lg: 14.5, xl: 16 },
	                      fontWeight: 500,
                        [SHORT_DESKTOP_QUERY]: {
                          paddingTop: 6,
                          fontSize: 12,
                        },
	                    }}
	                  >
                    Track. Monitor. Protect.
                  </Typography>
                </Stack>
              </Stack>

              <Box
	                sx={{
	                  position: "relative",
	                  width: "100%",
                    minWidth: 0,
                    maxWidth: 420,
	                  mt: { xs: 0.8, sm: 1.4, lg: 2.2, xl: 6.2 },
                    [SHORT_DESKTOP_QUERY]: {
                      marginTop: 2,
                    },
	                }}
	              >
                <Stack spacing={1.4} sx={{ width: "100%", alignItems: "stretch" }}>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 42, sm: 52, lg: 46, xl: 58 },
                        display: "block",
                        width: "100%",
	                      lineHeight: 1.04,
	                      fontWeight: 800,
	                      letterSpacing: "-0.05em",
	                      color: "#0B2239",
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 34,
                          lineHeight: 1.02,
                        },
	                    }}
	                  >
                    Welcome{" "}
                    <Box
                      component="span"
                      sx={{
                        backgroundImage: BRAND_GRADIENT,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      Back!
                    </Box>
                  </Typography>
                  <Typography
	                    sx={{
	                      color: "#667085",
	                      fontSize: { xs: 17, lg: 15.5, xl: 18 },
                        display: "block",
                        width: "100%",
                        lineHeight: 1.55,
	                      fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 12.5,
                        },
	                    }}
	                  >
                    Login to access your GPS tracking dashboard
                  </Typography>
                </Stack>

	                <Stack
                    spacing={2.3}
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      mt: { xs: 2.4, sm: 2.8, lg: 2.2 },
                      [SHORT_DESKTOP_QUERY]: {
                        gap: 8,
                        marginTop: 1.5,
                      },
                    }}
                  >
                {message && <Alert severity="success">{message}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}

                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                    User ID / Email / Mobile
                  </Typography>
                  <TextField
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    fullWidth
                    required
                    placeholder="Enter user id / email / mobile"
                    helperText={identifierHint}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IdentifierIcon sx={{ color: "#475467", fontSize: 24 }} />
                        </InputAdornment>
                      ),
                    }}
	                    sx={{
	                      "& .MuiOutlinedInput-root": {
	                        height: 58,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        fontSize: 16,
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                        "& fieldset": { borderColor: "#E5E7EB" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
	                        "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          [SHORT_DESKTOP_QUERY]: {
                            height: 44,
                            fontSize: 14,
                          },
	                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#98A2B3",
                        opacity: 1,
                      },
                      "& .MuiFormHelperText-root": {
                        mx: 0,
                        mt: 0.8,
                      },
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                    Password
                  </Typography>
                  <TextField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    fullWidth
                    required
                    placeholder="Enter your password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowPassword((s) => !s)}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon sx={{ color: "#475467" }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ color: "#475467" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
	                    sx={{
	                      "& .MuiOutlinedInput-root": {
	                        height: 58,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        fontSize: 16,
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                        "& fieldset": { borderColor: "#E5E7EB" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
	                        "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          [SHORT_DESKTOP_QUERY]: {
                            height: 44,
                            fontSize: 14,
                          },
	                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#98A2B3",
                        opacity: 1,
                      },
                    }}
                  />
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ pt: 0.2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Checkbox
                      size="small"
                      sx={{
                        color: "#98A2B3",
                        p: 0.25,
                        "&.Mui-checked": { color: "#22C55E" },
                      }}
                    />
                    <Typography sx={{ fontSize: 15, color: "#101828", fontWeight: 500 }}>
                      Remember Me
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#16A34A",
                    }}
                  >
                    Forgot Password
                  </Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  startIcon={!isLoading ? <LoginOutlinedIcon sx={{ fontSize: 22 }} /> : null}
	                  sx={{
	                    mt: 0.8,
	                    height: 60,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontFamily: AUTH_FONT_FAMILY,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#fff",
                    background: BRAND_GRADIENT,
                    boxShadow: "0 18px 30px rgba(37, 99, 235, 0.20)",
                    "&:hover": {
                      background: BRAND_GRADIENT,
                      boxShadow: "0 24px 40px rgba(37, 99, 235, 0.28)",
                    },
	                    "&.Mui-disabled": {
	                      color: "rgba(255,255,255,0.78)",
	                      background: BRAND_GRADIENT,
	                    },
                        [SHORT_DESKTOP_QUERY]: {
                          marginTop: 2,
                          height: 48,
                          fontSize: 15,
                        },
	                  }}
	                >
                  {isLoading ? (
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <CircularProgress size={18} color="inherit" />
                      <span>Logging in...</span>
                    </Stack>
                  ) : (
                    "Login"
                  )}
                </Button>

                </Stack>
              </Box>
            </Stack>

	            <Stack
                spacing={2.5}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  [SHORT_DESKTOP_QUERY]: {
                    gap: 4,
                  },
                }}
              >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: { xs: 1.5, lg: 0 },
	                  pt: { xs: 0.5, lg: 0.6, xl: 1.5 },
                    [SHORT_DESKTOP_QUERY]: {
                      paddingTop: 0,
                    },
	                }}
	              >
                {FEATURE_ITEMS.map(({ icon: Icon, title }, index) => (
                  <Stack
                    key={title}
                    alignItems="center"
                    spacing={1.3}
                    sx={{
                      textAlign: "center",
                      px: { xs: 1, lg: 1.4 },
                      py: 0.5,
                      borderRight: {
                        lg: index === FEATURE_ITEMS.length - 1 ? "none" : "1px solid #E4E7EC",
                      },
                    }}
                  >
                    <Box
	                      sx={{
	                        width: { xs: 48, lg: 42, xl: 48 },
	                        height: { xs: 48, lg: 42, xl: 48 },
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
	                        color: "#20B374",
	                        bgcolor: alpha("#22C55E", 0.08),
                            [SHORT_DESKTOP_QUERY]: {
                              width: 30,
                              height: 30,
                            },
	                      }}
	                    >
	                      <Icon
                          sx={{
                            fontSize: 26,
                            [SHORT_DESKTOP_QUERY]: {
                              fontSize: 17,
                            },
                          }}
                        />
	                    </Box>
	                    <Typography
	                      sx={{
	                        color: "#101828",
	                        fontSize: { xs: 15, lg: 14, xl: 16 },
	                        fontWeight: 600,
	                        lineHeight: 1.35,
                          [SHORT_DESKTOP_QUERY]: {
                            fontSize: 11.5,
                          },
	                      }}
	                    >
                      {title}
                    </Typography>
                  </Stack>
                ))}
              </Box>

              <Typography
	                sx={{
	                  color: "#667085",
	                  fontSize: 14,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", lg: "flex-start" },
	                  gap: 1,
	                  pt: { xs: 0, lg: 0.2, xl: 1 },
                    [SHORT_DESKTOP_QUERY]: {
                      fontSize: 11.5,
                      paddingTop: 0,
                    },
                }}
              >
                <ShieldOutlinedIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                © 2026 GPS Controller. All rights reserved.
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            flex: 1,
            minWidth: 0,
            minHeight: "100vh",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f8fafc",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            [SHORT_DESKTOP_QUERY]: {
              flexBasis: 0,
            },
          }}
        >
          <Box
            component="img"
            src={loginImage}
            alt="GPS tracking visual"
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: "100vh",
              objectFit: "contain",
              objectPosition: "center center",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
